import {
  CLIENT_METADATA_MIGRATIONS,
  CLIENT_SQL,
  ClientFileMetaSchema,
} from '../../src/metadata-db-sql'
import type {
  ClientBucketMetadataDb,
  ClientFileMeta,
} from '../../src/client-bucket-metadata-db'
import type { ListingCursor } from '../../src/server-bucket-metadata-db'
import type { DatabaseDriver } from '../../../db/src/core/types'
import { createTursoWorkerDriver } from '@w/db-turso-browser-adapter/worker'
import type { Tel } from '@w/tel'

export interface CreateTursoClientBucketMetadataDbOptions {
  bucketName: string
  tel: Tel
  makeDriver?: () => DatabaseDriver | Promise<DatabaseDriver>
}

export class TursoClientBucketMetadataDb implements ClientBucketMetadataDb {
  private driver: DatabaseDriver | null = null
  private initPromise: Promise<DatabaseDriver> | null = null

  constructor(
    public readonly bucketName: string,
    tel: Tel,
    private readonly makeDriver: () =>
      | DatabaseDriver
      | Promise<DatabaseDriver> = () =>
      createTursoWorkerDriver({
        path: `bucket-meta-${encodeURIComponent(bucketName)}.db`, tel,
      }),
  ) {}

  private init(tel: Tel): Promise<DatabaseDriver> {
    if (this.driver) return Promise.resolve(this.driver)

    this.initPromise ??= (async () => {
      const driver = await this.makeDriver()

      await driver.exec(`
        CREATE TABLE IF NOT EXISTS _bucket_meta_db_migrations (
          id INTEGER PRIMARY KEY CHECK (id = 1),
          version INTEGER NOT NULL
        )
      `, undefined, { tel })

      const rows = await driver.query<{ version: number }>(
        'SELECT version FROM _bucket_meta_db_migrations WHERE id = 1',
        undefined, { tel },
      )
      const currentVersion = rows[0]?.version ?? 0
      const targetVersion = CLIENT_METADATA_MIGRATIONS.length

      for (let i = currentVersion; i < targetVersion; i++) {
        await driver.exec(CLIENT_METADATA_MIGRATIONS[i], undefined, { tel })
      }

      if (currentVersion < targetVersion) {
        await driver.query(
          'INSERT OR REPLACE INTO _bucket_meta_db_migrations (id, version) VALUES (1, ?)',
          [targetVersion],
          { tel },
        )
      }

      this.driver = driver
      return driver
    })()

    return this.initPromise
  }

  async getFileMeta(path: string, tel: Tel): Promise<ClientFileMeta | null> {
    const driver = await this.init(tel)
    const rows = await driver.query<{
      uploadedAt: number | null
      synced: number
      deletedAt: number | null
      size: number
    }>(CLIENT_SQL.getFileMeta, [path], { tel })
    const raw = rows[0]
    if (!raw) return null

    return ClientFileMetaSchema.parse({
      ...(raw.uploadedAt === null ? {} : { uploadedAt: raw.uploadedAt }),
      synced: raw.synced === 1,
      deletedAt: raw.deletedAt,
      size: raw.size,
    })
  }

  async setFileMeta(path: string, meta: ClientFileMeta, tel: Tel): Promise<void> {
    const driver = await this.init(tel)
    const validated = ClientFileMetaSchema.parse(meta)
    await driver.query(CLIENT_SQL.setFileMeta, [
      path,
      validated.synced ? 1 : 0,
      validated.deletedAt,
      validated.size,
      validated.uploadedAt ?? null,
    ], { tel })
  }

  async deleteFileMeta(path: string, tel: Tel): Promise<void> {
    const driver = await this.init(tel)
    await driver.query(CLIENT_SQL.deleteFileMeta, [path], { tel })
  }

  async getUnsyncedFiles(tel: Tel): Promise<string[]> {
    const driver = await this.init(tel)
    const rows = await driver.query<{ path: string }>(
      CLIENT_SQL.getUnsyncedFiles,
      undefined, { tel },
    )
    return rows.map((row) => row.path)
  }

  async getAllFilePaths(tel: Tel): Promise<string[]> {
    const driver = await this.init(tel)
    const rows = await driver.query<{ path: string }>(
      CLIENT_SQL.getAllFilePaths,
      undefined, { tel },
    )
    return rows.map((row) => row.path)
  }

  async getScopedPullState(scope: string, tel: Tel): Promise<ListingCursor | null> {
    const driver = await this.init(tel)
    const rows = await driver.query<ListingCursor>(CLIENT_SQL.getScopedPullState, [scope], { tel })
    return rows[0] ?? null
  }

  async setScopedPullState(state: ListingCursor, tel: Tel): Promise<void> {
    if (!Number.isSafeInteger(state.after) || !Number.isSafeInteger(state.until) || state.after < 0 || state.after > state.until) throw new Error('Invalid listing checkpoint')
    const driver = await this.init(tel)
    await driver.query(CLIENT_SQL.setScopedPullState, [
      state.scope,
      state.epoch,
      state.after,
      state.until,
    ], { tel })
  }

  async clearScopedPullState(scope: string, tel: Tel): Promise<void> {
    const driver = await this.init(tel)
    await driver.query(CLIENT_SQL.clearScopedPullState, [scope], { tel })
  }

  async clear(tel: Tel): Promise<void> {
    const driver = await this.init(tel)
    await driver.exec(CLIENT_SQL.clear, undefined, { tel })
  }
}

export function createTursoClientBucketMetadataDb(
  options: CreateTursoClientBucketMetadataDbOptions,
): TursoClientBucketMetadataDb {
  return new TursoClientBucketMetadataDb(options.bucketName, options.tel, options.makeDriver)
}
